import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * VELOCITY — VariantPicker with compact-chip UX:
 * - Preserves your theme’s fetch/morph behavior
 * - Updates chip aria-selected states + legend “selected” text
 * - Auto-scrolls the chip rail to the active choice on mobile
 */
export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));
    // Initial UI sync for chips
    this.#syncChipUI();
    this.#scrollActiveIntoView();
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
    }
    if (loadsNewProduct) url.pathname = newUrl;

    if (url.href !== window.location.href) {
      requestYieldCallback(() => history.replaceState({}, '', url.toString()));
    }

    // Chip UX polish
    this.#syncChipUI();
    this.#scrollActiveIntoView();
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const el = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!el) throw new Error('Target element not found');
      target = el;
    }
    if (target instanceof HTMLInputElement) target.checked = true;

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const next = Array.from(target.options).find((o) => o.value === newValue);
      if (!next) throw new Error('Option not found');
      for (const o of target.options) o.removeAttribute('selected');
      next.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((r) => r.text())
      .then((htmlText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              }),
            );
          }
        }

        // After any morph, resync chip UI
        this.#syncChipUI();
        this.#scrollActiveIntoView();
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      });
  }

  updateVariantPicker(newHtml) {
    let newProduct;
    const src = newHtml.querySelector(this.tagName.toLowerCase());
    if (!src) throw new Error('No new variant picker source found');

    if (src instanceof HTMLElement) {
      const newProductId = src.dataset.productId;
      const newProductUrl = src.dataset.productUrl;
      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }
      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, src);
    return newProduct;
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  get selectedOption() {
    const node = this.querySelector('select option[selected], fieldset input:checked');
    if (!(node instanceof HTMLInputElement || node instanceof HTMLOptionElement)) return undefined;
    return node;
  }

  get selectedOptionId() {
    const node = this.selectedOption;
    if (!node) return undefined;
    const id = node.dataset.optionValueId;
    if (!id) throw new Error('No option value ID found');
    return id;
  }

  get selectedOptionsValues() {
    const nodes = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return nodes.map((n) => {
      const id = n.dataset.optionValueId;
      if (!id) throw new Error('No option value ID found');
      return id;
    });
  }

  /* ---------- UX helpers ---------- */

  #syncChipUI() {
    // aria-selected on .vx-chip & legend “selected” text
    this.querySelectorAll('.vx-chip').forEach((chip) => {
      const inputId = chip.getAttribute('for');
      const input = inputId ? this.querySelector(`#${inputId}`) : chip.querySelector('input[type="radio"]');
      const checked = input instanceof HTMLInputElement ? input.checked : chip.matches(':has(input:checked)');
      chip.setAttribute('aria-selected', checked ? 'true' : 'false');
    });

    this.querySelectorAll('fieldset').forEach((fs) => {
      const selected = fs.querySelector('input[type="radio"]:checked')?.value;
      const out = fs.querySelector('[data-vx-selected]');
      if (out && selected) out.textContent = selected;
    });
  }

  #scrollActiveIntoView() {
    // For mobile: ensure the selected chip is visible in its rail
    this.querySelectorAll('.vx-chips').forEach((rail) => {
      const sel = rail.querySelector('.vx-chip[aria-selected="true"]');
      if (!sel) return;
      const railRect = rail.getBoundingClientRect();
      const selRect = sel.getBoundingClientRect();
      const isOverflowing = rail.scrollWidth > rail.clientWidth + 4;
      if (!isOverflowing) return;

      const deltaLeft = selRect.left - railRect.left;
      const deltaRight = selRect.right - railRect.right;
      if (deltaLeft < 0 || deltaRight > 0) {
        rail.scrollBy({ left: deltaLeft - 12, behavior: 'smooth' });
      }
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}